import React from 'react';
import styled from 'styled-components';
import BoxBlock from '@component/common/block/box-block';
import IconBlock from '@component/common/block/icon-block';
import TextBlock from '@component/common/block/text-block';
import { useAudioPlayer } from '@hook/common/useAudioPlayer';
import * as color from '@constant/color';
import * as size from '@constant/size';
import { ETheme } from '@datatype/type';

interface IProps {
  theme?: ETheme;
  kind?: 'filled' | 'outlined';
  size?: 'small' | 'medium' | 'large';
  shape?: 'round' | 'square';
  width?: string;
  height?: string;
  sources: string[];
  timing?: {
    begin?: number;
    gap?: number;
    end?: number;
  };
  replay?: boolean;
}

const Wrapper = styled.div<{ width: string; height: string }>`
  width: ${(props) => props.width};
  height: ${(props) => props.height};
`;

const createDesign = (
  theme: ETheme,
  btnSize: 'small' | 'medium' | 'large',
  kind: 'filled' | 'outlined',
  shape: 'round' | 'square',
) => {
  const themeColor = theme === ETheme.BROWN ? color.BROWN_1 : color.BLACK_2;

  let backgroundColor = themeColor;
  let textColor = color.WHITE_1;
  const borderColor = themeColor;
  let iconColor = color.WHITE_1;

  if (kind === 'outlined') {
    backgroundColor = color.WHITE_1;
    textColor = themeColor;
    iconColor = themeColor;
  }

  const boxDesign = {
    alignX: 'center',
    backgroundColor: backgroundColor,
    borderRadius: shape === 'round' ? size.DESIGN_SYSTEM_BUTTON_BORDER_RADIUS : '0px',
    event: { cursor: 'pointer' },
    width: '100%',
    height:
      btnSize === 'large'
        ? size.DESIGN_SYSTEM_BUTTON_LARGE_HEIGHT
        : btnSize === 'medium'
          ? size.DESIGN_SYSTEM_BUTTON_MEDIUM_HEIGHT
          : size.DESIGN_SYSTEM_BUTTON_SMALL_HEIGHT,
    border: kind === 'outlined',
    borderWidth: size.DESIGN_SYSTEM_BUTTON_BORDER_WIDTH,
    borderColor: borderColor,
    gap: '5px',
  } as const;

  const textDesign = {
    size: btnSize,
    color: textColor,
  } as const;

  const iconDesign = {
    size: btnSize,
    kind,
    color: iconColor,
  } as const;

  return { box: boxDesign, text: textDesign, icon: iconDesign };
};

const TextAudioButton: React.FC<IProps> = ({
  theme = ETheme.BROWN,
  kind = 'filled',
  size = 'medium',
  shape = 'square',
  width = 'fit-content',
  height = 'auto',
  sources,
  timing = {},
  replay = false,
}) => {
  const design = createDesign(theme, size, kind, shape);

  const {
    isPlaying,
    isReplayable,
    audioRefs,
    handlePlayCompleted,
    handlePlayAndPauseClick,
    handleReplayClick,
  } = useAudioPlayer({ sources, timing });

  return (
    <Wrapper width={width} height={height}>
      <BoxBlock design={{ box: design.box }} onClick={handlePlayAndPauseClick}>
        <TextBlock text={isPlaying ? '일시정지' : '듣기'} design={{ text: design.text }} />
        {replay && isReplayable && (
          <IconBlock
            icon={'replay'}
            design={{
              icon: design.icon,
              box: { event: { cursor: 'pointer' } },
            }}
            click={{
              handler: handleReplayClick,
            }}
          />
        )}
        {sources &&
          sources.map((src, idx) => (
            <audio
              key={idx}
              ref={(element) => {
                if (element) audioRefs.current[idx] = element;
              }}
              src={src}
              onEnded={handlePlayCompleted}
              controls={false}
            />
          ))}
      </BoxBlock>
    </Wrapper>
  );
};

export default TextAudioButton;
